import {
  CalendarMonth,
  ErrorOutline,
  History,
  LocationOn,
  NoteAlt,
  Person,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Skeleton,
  Stack,
  Typography,
  useTheme,
  Link,
} from "@mui/material";
import React from "react";
import { useSelector } from "react-redux";
import { IAcitvityHistoryLogsProps } from "../../../../components/Activity/Activity.types";
import { IStoreState } from "../../../../redux/initialStoreState";
import { IGuestVisitHistory } from "../../../../redux/student/student.types";
import { useDispatchWrapper } from "../../../../hooks";
import { ERROR_MESSAGES, LoadState } from "../../../../constants/enums";
import { formatText } from "../../../../helpers";
import { fetchuestVisitHistoryAsync } from "../../../../redux/student/studentActions";
import { useBillingCompanyContext } from "../../../../providers";
import { StatusRenderer } from "../../../../components/ActiveInActiveStatus/ActiveInActiveStatus";
import { useTimezoneFormatter } from "../../../../hooks/useTimezoneFormatter";
import { GuestHistoryPopoverContent } from "./GuestHistoryPopoverContent";

export const GuestVisitHistory: React.FC<IAcitvityHistoryLogsProps> = (
  props,
) => {
  const { customerFactUuid, moduleInformation } = props;

  const { data: guestVisitHistory, loading } = useSelector(
    (storeState: IStoreState) => storeState.student.guestVisitHistory,
  );

  const [selectedHistory, setSelectedHistory] =
    React.useState<IGuestVisitHistory | null>(null);
  const [open, setOpen] = React.useState(false);
  const theme = useTheme();

  const dispatch = useDispatchWrapper();
  const company = useBillingCompanyContext();

  const fetchHistory = async () => {
    dispatch(
      fetchuestVisitHistoryAsync(
        customerFactUuid || "",
        company.comapny.company_outlet_uuid,
      ),
    );
  };

  React.useEffect(() => {
    fetchHistory();
  }, []);

  const handleClick = (
    event: React.MouseEvent<HTMLElement>,
    history: IGuestVisitHistory | any,
  ) => {
    setSelectedHistory(history);
  };

  const handleShowMore = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Stack mt={3}>
        <Stack
          padding={1}
          sx={{
            backgroundColor: theme.palette.grey[200],
            borderRadius: 1,
            display: "flex",
            flexDirection: "row",
            gap: 1,
          }}
        >
          <Person
            color="primary"
            fontSize="medium"
            sx={{ marginTop: "auto", marginBottom: "auto" }}
          />
          <Typography variant="h4" fontWeight={600}>
            Guest Interaction Logs
          </Typography>
        </Stack>
        <Stack direction={"column"} spacing={1} mt={3}>
          {loading === LoadState.InProgress && (
            <>
              <Skeleton height={"30px"} />
              <Skeleton height={"30px"} />
              <Skeleton height={"30px"} />
              <Skeleton height={"30px"} />
              <Skeleton height={"30px"} />
            </>
          )}
          {loading === LoadState.Loaded && guestVisitHistory.length === 0 && (
            <Box
              minHeight={100}
              display={"flex"}
              flexDirection={"column"}
              justifyContent={"center"}
              alignItems={"center"}
            >
              <Box display={"flex"} justifyContent={"center"} mb={1}>
                <ErrorOutline />
              </Box>
              <Box display={"flex"} justifyContent={"center"} width={"80%"}>
                <Typography
                  variant="body1"
                  textAlign={"center"}
                  fontWeight={600}
                >
                  {ERROR_MESSAGES.NO_RECORD_FOUND}
                </Typography>
              </Box>
            </Box>
          )}

          {loading === LoadState.Loaded &&
            guestVisitHistory.slice(0, 4).map((item, index) => {
              return (
                <HistoryRow
                  key={index}
                  data={item}
                  selection={(e) => handleClick(e, item)}
                />
              );
            })}

          {loading === LoadState.Loaded && guestVisitHistory.length > 4 && (
            <Button
              variant="outlined"
              onClick={handleShowMore}
              sx={{ marginTop: 2 }}
            >
              Show More
            </Button>
          )}
        </Stack>
      </Stack>

      {open && (
        <GuestHistoryPopoverContent
          data={guestVisitHistory}
          open={open}
          onClose={handleClose}
        />
      )}
    </>
  );
};

export const HistoryRow: React.FC<{
  data: IGuestVisitHistory | any;
  selection?: (event: React.MouseEvent<HTMLElement>) => void;
}> = ({ data, selection }) => {
  const theme = useTheme();
  const { ftDateTime } = useTimezoneFormatter();

  const MAX_REMARK_LENGTH = 100;
  const remark = data.remarks || "";
  const isRemarkLong = remark.length > MAX_REMARK_LENGTH;
  const truncatedRemark = isRemarkLong
    ? remark.substring(0, MAX_REMARK_LENGTH) + "..."
    : remark;

  const handleIconClick = (e: React.MouseEvent<SVGSVGElement>) => {
    if (selection) {
      const htmlEvent = e as unknown as React.MouseEvent<HTMLElement>;
      selection(htmlEvent);
    }
  };

  const handleReadMoreClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    if (selection) {
      selection(e);
    }
  };

  return (
    <Stack position={"relative"} minHeight={"80px"}>
      <Stack
        direction={"row"}
        spacing={1}
        sx={{
          ":before": {
            content: "''",
            height: "100%",
            width: "2px",
            position: "absolute",
            left: "3%",
            top: 0,
            bottom: 0,
            zIndex: 0,
            backgroundColor: theme.palette.success.main,
          },
        }}
      >
        <Box
          border={"1px solid #fff"}
          sx={{ backgroundColor: "#fff" }}
          zIndex={1}
          height={"fit-content"}
        >
          <History
            sx={{ cursor: "pointer" }}
            onClick={handleIconClick}
            fontSize="medium"
            color="success"
          />
        </Box>
        <Stack width={"100%"}>
          <Stack direction={"row"} justifyContent={"space-between"}>
            <StatusRenderer status={data.status || ""} />
          </Stack>

          <Stack direction={"row"} alignItems={"center"}>
            <Person sx={{ fontSize: 14, mr: 0.5 }} />
            <Typography variant="body1" fontWeight={100}>
              {data.modified_by_name} has updated this customer
            </Typography>
          </Stack>

          <Stack direction={"row"} flexWrap={"wrap"} alignItems={"center"}>
            <LocationOn sx={{ fontSize: 14, mr: 0.5 }} />
            <Typography variant="body2" fontSize={"12px"} fontWeight={600}>
              Venue Name: {formatText(data.venue_name || "--")}
            </Typography>
          </Stack>

          <Stack direction={"row"} flexWrap={"wrap"} alignItems={"center"}>
            <CalendarMonth sx={{ fontSize: 14, mr: 0.5 }} />
            <Typography variant="body2" fontSize={"12px"} fontWeight={600}>
              Created Date: {ftDateTime(data.create_ts)}
            </Typography>
          </Stack>

          <Stack direction={"row"} flexWrap={"wrap"} alignItems={"center"}>
            <CalendarMonth sx={{ fontSize: 14, mr: 0.5 }} />
            <Typography variant="body2" fontSize={"12px"} fontWeight={600}>
              Booking Date: {ftDateTime(data.booking_date)}
            </Typography>
          </Stack>
          <Stack direction={"row"} flexWrap="wrap" alignItems={"center"}>
            <NoteAlt sx={{ fontSize: 14, mr: 0.5 }} />
            <Typography
              variant="body1"
              fontWeight={600}
              sx={{ fontStyle: "italic" }}
            >
              {truncatedRemark}
              {isRemarkLong && (
                <Link
                  component="button"
                  variant="body2"
                  onClick={handleReadMoreClick}
                  sx={{
                    ml: 0.5,
                    cursor: "pointer",
                    textDecoration: "none",
                    color: theme.palette.success.main,
                    fontWeight: 600,
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  Read More
                </Link>
              )}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
